/**
 * <custom-account-menu> — the signed-in / signed-out account UI rendered inside
 * the header popover (desktop) and drawer (mobile). Driven entirely by the
 * window.CKCAuth controller (assets/custom-auth.js); the storefront has no
 * Shopify `customer` session when native accounts are disabled.
 *
 * Signed-in is a set of in-panel "views" (the popover is roughly a phone-sized
 * surface, so this mirrors the mobile app's screen flow):
 *   - account:      rewards teaser → order-history preview (3) → sign out
 *   - orders:       the full order list ("View all")
 *   - order-detail: one order, email-style (line items, totals, ship-to)
 *   - settings:     display name + preferred email (device-local) → delete account
 *
 * Multiple instances (popover + drawer) can be on the page at once; each
 * subscribes to CKCAuth.onChange so they stay in sync.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PREVIEW_COUNT = 3;

// Shipping-address option lists. Values are the CODES Shopify expects
// (countryCode / provinceCode); only US/CA get a province dropdown, others fall
// back to a free-text province-code input. Keep in sync with the app's
// src/data/addressOptions.ts.
const COUNTRIES = [
  ['US', 'United States'], ['CA', 'Canada'], ['GB', 'United Kingdom'],
  ['AU', 'Australia'], ['IE', 'Ireland'], ['NZ', 'New Zealand'],
];
const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'],
  ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'],
  ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'],
  ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'], ['SD', 'South Dakota'],
  ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'],
  ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const CA_PROVINCES = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NT', 'Northwest Territories'], ['NS', 'Nova Scotia'],
  ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
];
function provincesFor(countryCode) {
  if (countryCode === 'US') return US_STATES;
  if (countryCode === 'CA') return CA_PROVINCES;
  return null;
}
const ADDR_KEYS = ['firstName', 'lastName', 'address1', 'address2', 'city', 'countryCode', 'provinceCode', 'zip', 'phone'];

class CustomAccountMenu extends HTMLElement {
  connectedCallback() {
    const auth = window.CKCAuth;
    this.innerHTML = this.template();
    this.ordersData = [];
    this.ordersLoaded = false;
    this.currentView = 'account';
    this.detailReturn = 'account';
    this.refs = {
      loading: this.querySelector('[data-state="loading"]'),
      signedOut: this.querySelector('[data-state="signed-out"]'),
      signedIn: this.querySelector('[data-state="signed-in"]'),
      greeting: this.querySelector('[data-greeting]'),
      email: this.querySelector('[data-email]'),
      orders: this.querySelector('[data-orders]'),
      ordersViewAll: this.querySelector('[data-orders-viewall]'),
      ordersAll: this.querySelector('[data-orders-all]'),
      orderDetail: this.querySelector('[data-order-detail]'),
      detailTitle: this.querySelector('[data-detail-title]'),
      apple: this.querySelector('[data-action="apple"]'),
      emailBlock: this.querySelector('[data-email-block]'),
      emailStep: this.querySelector('[data-email-step="email"]'),
      codeStep: this.querySelector('[data-email-step="code"]'),
      emailInput: this.querySelector('[data-email-input]'),
      codeInput: this.querySelector('[data-code-input]'),
      emailSent: this.querySelector('[data-email-sent]'),
      emailError: this.querySelector('[data-email-error]'),
      codeError: this.querySelector('[data-code-error]'),
      signoutBtn: this.querySelector('[data-signout-trigger]'),
      signoutConfirm: this.querySelector('[data-signout-confirm]'),
      accountView: this.querySelector('[data-view="account"]'),
      rewardsSubtitle: this.querySelector('[data-rewards-subtitle]'),
      ordersView: this.querySelector('[data-view="orders"]'),
      detailView: this.querySelector('[data-view="order-detail"]'),
      settingsView: this.querySelector('[data-view="settings"]'),
      nameInput: this.querySelector('[data-name-input]'),
      prefEmailInput: this.querySelector('[data-prefemail-input]'),
      prefEmailError: this.querySelector('[data-prefemail-error]'),
      emailVerify: this.querySelector('[data-email-verify]'),
      evSendStep: this.querySelector('[data-evstep="send"]'),
      evCodeStep: this.querySelector('[data-evstep="code"]'),
      evCode: this.querySelector('[data-ev-code]'),
      evSent: this.querySelector('[data-ev-sent]'),
      evError: this.querySelector('[data-ev-error]'),
      evVerified: this.querySelector('[data-ev-verified]'),
      saveBtn: this.querySelector('[data-action="save-settings"]'),
      settingsError: this.querySelector('[data-settings-error]'),
      settingsSaved: this.querySelector('[data-settings-saved]'),
      addrSave: this.querySelector('[data-addr-save]'),
      addrRemove: this.querySelector('[data-addr-remove]'),
      addrError: this.querySelector('[data-addr-error]'),
      addrSaved: this.querySelector('[data-addr-saved]'),
      provinceField: this.querySelector('[data-province-field]'),
      addrAc: this.querySelector('[data-addr-ac]'),
      deleteBtn: this.querySelector('[data-delete-trigger]'),
      deleteConfirm: this.querySelector('[data-delete-confirm]'),
      deleteError: this.querySelector('[data-delete-error]'),
      error: this.querySelector('[data-error]'),
    };

    // Show the "added to cart / skipped items" banner after a Buy-again redirect.
    this.maybeShowBuyAgainFlash();

    if (!auth) {
      this.setState('signed-out');
      return;
    }

    if (!auth.appleEnabled() && this.refs.apple) this.refs.apple.hidden = true;
    if (!auth.emailEnabled() && this.refs.emailBlock) this.refs.emailBlock.hidden = true;

    this.addEventListener('click', this.onClick.bind(this));
    // Enter submits the focused field (the inputs aren't in a <form>).
    this.addEventListener('keydown', this.onKeydown.bind(this));
    // Live-update the settings Save button + email verify UI as the user types.
    this.addEventListener('input', this.onInput.bind(this));
    // <select> (country/province) fires 'change', not 'input'.
    this.addEventListener('change', this.onChange.bind(this));
    // Auto-hide scrollbars: flag a scroll container while it's actively
    // scrolling so the (gutter-reserved) bar only paints during scroll.
    // Scroll doesn't bubble, so listen in the capture phase.
    this.addEventListener('scroll', this.onScroll.bind(this), true);
    // The nested <rewards-account> module opens its own in-panel "pages" (redeem
    // / full activity). It emits this when a sub-page opens or closes so we can
    // hide the rest of the account view and let it read as a full page (mirrors
    // how order history's "View all" takes over the panel).
    this.addEventListener('rewards:screen', this.onRewardsScreen.bind(this));
    this.unsubscribe = auth.onChange(this.render.bind(this));

    // Track the mode the template() render produced (coming-soon by default,
    // since the launch config loads asynchronously), then subscribe to the
    // launch flag so the section swaps to the live module if/when it flips true.
    this._rewardsMode = this.rewardsEnabled() && this.rewardsLaunched() ? 'live' : 'soon';
    if (typeof auth.onRewardsConfig === 'function') {
      this.unsubscribeConfig = auth.onRewardsConfig(this.renderRewardsSlot.bind(this));
    }
  }

  onScroll(event) {
    const el = event.target;
    if (!(el instanceof HTMLElement)) return;
    if (!this._scrollTimers) this._scrollTimers = new WeakMap();
    el.classList.add('is-scrolling');
    clearTimeout(this._scrollTimers.get(el));
    this._scrollTimers.set(
      el,
      setTimeout(() => el.classList.remove('is-scrolling'), 700)
    );
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.unsubscribeConfig) this.unsubscribeConfig();
  }

  setState(state) {
    this.refs.loading.hidden = state !== 'loading';
    this.refs.signedOut.hidden = state !== 'signed-out';
    this.refs.signedIn.hidden = state !== 'signed-in';
  }

  render(user, ready) {
    if (!ready) {
      this.setState('loading');
      return;
    }
    if (user) {
      // Only reset transient UI (view, confirmations, form inputs) when first
      // entering the signed-in state — NOT on every emit (e.g. a settings save
      // emits to refresh the greeting, and we don't want that to bounce the user
      // off the settings view or wipe the "Saved ✓" message).
      const entering = this.refs.signedIn.hidden;
      const profile = (window.CKCAuth.getProfile && window.CKCAuth.getProfile()) || {};
      const name =
        profile.displayName || user.firstName || (user.email ? user.email.split('@')[0] : '');
      this.refs.greeting.textContent = name ? `Hi, ${name}` : 'Your account';
      const shownEmail = profile.preferredEmail || user.email || '';
      this.refs.email.textContent = shownEmail;
      this.refs.email.hidden = !shownEmail;
      if (entering) {
        this.resetSettingsForm();
        this.resetSignout();
        this.resetDelete();
        if (this.refs.accountView) this.refs.accountView.classList.remove('is-rewards-subpage');
        this.showView('account');
      }
      this.setState('signed-in');
      this.loadOrders();
    } else {
      this.resetEmail();
      this.setState('signed-out');
    }
  }

  // ---- orders ----
  async loadOrders() {
    const el = this.refs.orders;
    if (!el) return;
    if (this.ordersLoaded) {
      this.renderPreview();
      return;
    }
    el.innerHTML = '<p class="custom-account__muted">Loading your orders…</p>';
    try {
      this.ordersData = await window.CKCAuth.getOrders();
      this.ordersLoaded = true;
      this.renderPreview();
    } catch (e) {
      el.innerHTML = '<p class="custom-account__muted">Could not load orders right now.</p>';
    }
  }

  renderPreview() {
    const el = this.refs.orders;
    if (!el) return;
    const orders = this.ordersData || [];
    const hasMore = orders.length > PREVIEW_COUNT;
    if (this.refs.ordersViewAll) {
      this.refs.ordersViewAll.hidden = !hasMore;
      this.refs.ordersViewAll.textContent = `View all (${orders.length})`;
    }
    if (!orders.length) {
      el.innerHTML = '<p class="custom-account__muted">You have no orders yet.</p>';
      return;
    }
    const rows = orders.slice(0, PREVIEW_COUNT).map((o, i) => this.orderRow(o, i > 0)).join('');
    const viewAll = hasMore
      ? `<button type="button" class="custom-account__viewall" data-action="orders-all">View all ${orders.length} orders →</button>`
      : '';
    el.innerHTML = rows + viewAll;
  }

  renderAllOrders() {
    const el = this.refs.ordersAll;
    if (!el) return;
    const orders = this.ordersData || [];
    el.innerHTML = orders.length
      ? orders.map((o, i) => this.orderRow(o, i > 0)).join('')
      : '<p class="custom-account__muted">You have no orders yet.</p>';
  }

  // Mirrors the app's OrderRow: number + total on top, date + status badge,
  // then a one/two-line item summary.
  orderRow(o, divider) {
    const items = (o.items || [])
      .map((li) => `${li.quantity}× ${this.escape(li.title)}${li.variant ? ` (${this.escape(li.variant)})` : ''}`)
      .join(', ');
    return (
      `<button type="button" class="custom-account__orderrow${divider ? ' has-divider' : ''}" data-action="order-open" data-order-id="${this.escape(o.id)}">` +
      '<span class="custom-account__orderrow-top">' +
      `<span class="custom-account__orderrow-number">${this.escape(o.name || '')}</span>` +
      `<span class="custom-account__orderrow-total">${this.money(o.total)}</span>` +
      '</span>' +
      '<span class="custom-account__orderrow-meta">' +
      `<span class="custom-account__orderrow-date">${this.formatDate(o.processedAt)}</span>` +
      this.fulfillmentBadge(o.fulfillment) +
      this.statusBadge(o.status || o.fulfillmentStatus) +
      '</span>' +
      (items ? `<span class="custom-account__orderrow-items">${items}</span>` : '') +
      '</button>'
    );
  }

  // Mirrors the app's OrderDetailScreen (header → line items + totals → ship-to),
  // limited to the fields the orders API returns (no payment/timeline).
  renderDetail(order) {
    this.detailOrder = order;
    if (this.refs.detailTitle) this.refs.detailTitle.textContent = `Order ${order.name || ''}`.trim();
    const itemCount = (order.items || []).reduce((n, li) => n + (li.quantity || 0), 0);

    const items = (order.items || [])
      .map((li) => {
        const unit = this.money(li.unitPrice);
        const lineTotal =
          li.unitPrice && li.unitPrice.amount != null
            ? this.money({ amount: Number(li.unitPrice.amount) * li.quantity, currencyCode: li.unitPrice.currencyCode })
            : '';
        const thumb =
          '<span class="custom-account__item-thumb">' +
          (li.imageUrl ? `<img src="${this.escape(li.imageUrl)}" alt="" loading="lazy">` : '') +
          `<span class="custom-account__item-qtybadge">${li.quantity}</span>` +
          '</span>';
        return (
          '<div class="custom-account__item">' +
          thumb +
          '<span class="custom-account__item-info">' +
          `<span class="custom-account__item-title">${this.escape(li.title)}</span>` +
          (li.variant ? `<span class="custom-account__item-variant">${this.escape(li.variant)}</span>` : '') +
          (unit ? `<span class="custom-account__item-unit">${unit}/ea</span>` : '') +
          '</span>' +
          (lineTotal ? `<span class="custom-account__item-total">${lineTotal}</span>` : '') +
          '</div>'
        );
      })
      .join('');

    const hasDiscount = order.discount && Number(order.discount.amount) > 0;
    const shippingFree = order.shipping && Number(order.shipping.amount) === 0;
    const totals =
      '<div class="custom-account__totals">' +
      (order.subtotal
        ? this.totalRow(`Subtotal · ${itemCount} item${itemCount === 1 ? '' : 's'}`, this.money(order.subtotal))
        : '') +
      (hasDiscount
        ? this.totalRow(
            order.discountCode ? `Discount (${order.discountCode})` : 'Discount',
            '−' + this.money(order.discount),
            false,
            true
          )
        : '') +
      (order.shipping ? this.totalRow('Shipping', shippingFree ? 'Free' : this.money(order.shipping)) : '') +
      this.totalRow('Total', this.money(order.total), true) +
      '</div>';

    const f = order.fulfillment;
    let fulfillmentRows = '';
    if (f) {
      if (f.method === 'pickup') {
        fulfillmentRows = this.infoRow('Pickup', f.when || 'Local pickup');
        if (f.address) {
          // Address row whose value column holds the address plus stacked
          // shortcuts: copy it for any map app, or jump to it on Google Maps.
          const mapUrl =
            'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(f.address);
          fulfillmentRows +=
            '<div class="custom-account__info-row">' +
            '<span class="custom-account__info-label">Pickup address</span>' +
            '<span class="custom-account__info-value">' +
            this.escape(f.address).replace(/\n/g, '<br>') +
            '<span class="custom-account__pickup-actions">' +
            '<button type="button" class="custom-account__maplink" data-action="copy-address">Copy address</button>' +
            `<a class="custom-account__maplink" href="${this.escape(mapUrl)}" target="_blank" rel="noopener">Open in Google Maps</a>` +
            '</span>' +
            '</span>' +
            '</div>';
        }
      } else {
        fulfillmentRows = this.infoRow('Delivery', f.when || 'Delivery');
      }
    }

    const infoRows =
      fulfillmentRows +
      (order.email ? this.infoRow('Contact', order.email) : '') +
      (order.shipTo
        ? this.infoRow('Ship to', [order.shipTo.name].concat(order.shipTo.lines || []).filter(Boolean).join('\n'))
        : '');

    // Delivery proof — signature and/or photo captured at drop-off (only
    // present on orders the delivery-signatures app recorded one for).
    const proof = order.deliveryProof;
    let proofCard = '';
    if (proof && (proof.signatureUrl || proof.photoUrl)) {
      const caption =
        (proof.recipientName ? `Signed by ${this.escape(proof.recipientName)}` : 'Signed on delivery') +
        (proof.timestamp
          ? ' · ' +
            this.escape(
              new Date(proof.timestamp).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            )
          : '');
      proofCard =
        '<div class="custom-account__card">' +
        '<div class="custom-account__proof-label">Delivered</div>' +
        (proof.photoUrl
          ? `<img class="custom-account__proof-photo" src="${this.escape(proof.photoUrl)}" alt="Delivery photo" loading="lazy">`
          : '') +
        (proof.signatureUrl
          ? `<div class="custom-account__proof-sigbox"><img class="custom-account__proof-signature" src="${this.escape(proof.signatureUrl)}" alt="Delivery signature" loading="lazy"></div>`
          : '') +
        `<div class="custom-account__proof-caption">${caption}</div>` +
        '</div>';
    }

    this.refs.orderDetail.innerHTML =
      `<div class="custom-account__detail-meta"><span class="custom-account__orderrow-date">${this.formatDate(order.processedAt)}</span>${this.statusBadge(order.status || order.fulfillmentStatus)}</div>` +
      proofCard +
      `<div class="custom-account__card">${items}<div class="custom-account__divider-line"></div>${totals}</div>` +
      (infoRows ? `<div class="custom-account__card custom-account__info">${infoRows}</div>` : '') +
      '<button type="button" class="button button-secondary custom-account__buyagain" data-action="buy-again">Buy again</button>' +
      '<p class="custom-account__muted custom-account__buyagain-msg" data-buyagain-msg hidden></p>';
  }

  totalRow(label, value, grand, discount) {
    const cls = grand ? ' is-grand' : discount ? ' is-discount' : '';
    return (
      `<div class="custom-account__total-row${cls}">` +
      `<span>${this.escape(label)}</span><span>${this.escape(value)}</span></div>`
    );
  }

  // Buy again → add the order's still-available items to the cart, skip the
  // rest, and flash which were skipped on the cart page.
  async buyAgain(trigger) {
    const order = this.detailOrder;
    if (!order) return;
    const items = order.items || [];
    const addable = items.filter((li) => li.variantId);
    const skipped = items.filter((li) => !li.variantId).map((li) => li.title);

    if (!addable.length) {
      this.showBuyAgainMsg(
        skipped.length
          ? 'These items are no longer available to re-order.'
          : "We couldn't find these items to re-order."
      );
      return;
    }

    if (trigger) trigger.disabled = true;
    let added = 0;
    for (const li of addable) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: li.variantId, quantity: li.quantity }),
        });
        if (res.ok) added += 1;
        else skipped.push(li.title); // sold out / unavailable
      } catch (e) {
        skipped.push(li.title);
      }
    }
    if (trigger) trigger.disabled = false;

    if (!added) {
      this.showBuyAgainMsg('Sorry, these items are no longer available to re-order.');
      return;
    }
    try {
      sessionStorage.setItem('ckc.buyagain', JSON.stringify({ skipped }));
    } catch (e) {
      /* ignore */
    }
    window.location.href = '/cart';
  }

  showBuyAgainMsg(msg) {
    const el = this.querySelector('[data-buyagain-msg]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  // On the cart page after a Buy-again redirect, show what was added/skipped.
  maybeShowBuyAgainFlash() {
    if (!/^\/cart/.test(window.location.pathname)) return;
    let flash = null;
    try {
      flash = JSON.parse(sessionStorage.getItem('ckc.buyagain') || 'null');
    } catch (e) {
      /* ignore */
    }
    if (!flash) return; // first instance to run consumes it
    sessionStorage.removeItem('ckc.buyagain');
    const skipped = flash.skipped || [];
    const text = skipped.length
      ? `Your previous items were added. Skipped (no longer available): ${skipped.join(', ')}.`
      : 'Your previous items were added to the cart.';
    const bar = document.createElement('div');
    bar.className = 'ckc-flash';
    bar.setAttribute('role', 'status');
    const span = document.createElement('span');
    span.textContent = text;
    const close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', () => bar.remove());
    bar.appendChild(span);
    bar.appendChild(close);
    document.body.appendChild(bar);
    setTimeout(() => bar.remove(), 9000);
  }
  infoRow(label, value) {
    return (
      '<div class="custom-account__info-row">' +
      `<span class="custom-account__info-label">${this.escape(label)}</span>` +
      `<span class="custom-account__info-value">${this.escape(value).replace(/\n/g, '<br>')}</span></div>`
    );
  }

  // Copy text to the clipboard, with a legacy textarea fallback for browsers
  // without the async Clipboard API (or when it's blocked).
  async copyText(text) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      /* fall through to legacy path */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  }

  money(m) {
    if (!m || m.amount == null) return '';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: m.currencyCode || 'USD',
      }).format(m.amount);
    } catch (e) {
      return `$${Number(m.amount).toFixed(2)}`;
    }
  }

  formatDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  // Accepts the server-derived `status` (preferred — includes the
  // notification-driven pickup_ready / out_for_delivery stages) and falls back
  // to raw Shopify displayFulfillmentStatus values. Call sites pass
  // `o.status || o.fulfillmentStatus`.
  statusBadge(status) {
    const s = String(status || '').toUpperCase();
    let label;
    let cls;
    if (s === 'FULFILLED' || s === 'DELIVERED') {
      label = 'Delivered';
      cls = 'is-done';
    } else if (s === 'PICKUP_READY') {
      label = 'Ready for Pickup';
      cls = 'is-active';
    } else if (s === 'OUT_FOR_DELIVERY') {
      label = 'Out for Delivery';
      cls = 'is-active';
    } else if (s === 'IN_PROGRESS' || s === 'PARTIALLY_FULFILLED' || s === 'SCHEDULED') {
      label = 'In progress';
      cls = 'is-active';
    } else if (s === 'UNFULFILLED' || s === 'PREPARING' || s === '') {
      label = 'Preparing';
      cls = 'is-neutral';
    } else {
      label = s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
      cls = 'is-neutral';
    }
    return `<span class="custom-account__badge ${cls}">${this.escape(label)}</span>`;
  }

  // Small pill showing whether the order was a local pickup or a delivery.
  fulfillmentBadge(f) {
    if (!f || !f.method) return '';
    const label = f.method === 'pickup' ? 'Pickup' : 'Delivery';
    return `<span class="custom-account__badge custom-account__badge--${f.method}">${label}</span>`;
  }

  escape(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  async onClick(event) {
    const trigger = event.target.closest('[data-action]');
    if (!trigger) return;
    const action = trigger.dataset.action;
    event.preventDefault();
    this.clearError();

    const errEl =
      action === 'email-start'
        ? this.refs.emailError
        : action === 'email-verify'
        ? this.refs.codeError
        : action === 'save-settings'
        ? this.refs.settingsError
        : action === 'profile-email-send' || action === 'profile-email-verify'
        ? this.refs.evError
        : action === 'delete-confirm'
        ? this.refs.deleteError
        : action === 'save-address' || action === 'remove-address'
        ? this.refs.addrError
        : this.refs.error;

    try {
      if (action === 'google') {
        trigger.disabled = true;
        await window.CKCAuth.signInWithGoogle(); // redirects away
      } else if (action === 'apple') {
        trigger.disabled = true;
        await window.CKCAuth.signInWithApple();
      } else if (action === 'email-start') {
        const email = (this.refs.emailInput.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          this.showError('Enter a valid email address.', errEl);
          return;
        }
        trigger.disabled = true;
        await window.CKCAuth.requestEmailCode(email);
        this.showCodeStep(email);
      } else if (action === 'email-verify') {
        const code = (this.refs.codeInput.value || '').replace(/\s+/g, '');
        if (!code) {
          this.showError('Enter the code we emailed you.', errEl);
          return;
        }
        trigger.disabled = true;
        await window.CKCAuth.verifyEmailCode(code); // emits → render() switches to signed-in
      } else if (action === 'email-restart') {
        this.resetEmail();
        if (this.refs.emailInput) this.refs.emailInput.focus();
      } else if (action === 'orders-all') {
        this.renderAllOrders();
        this.showView('orders');
      } else if (action === 'orders-back') {
        this.showView('account');
      } else if (action === 'rewards-back') {
        // The rewards sub-page header lives here in the parent; hand control back
        // to the module so it returns to its main screen (and emits 'main', which
        // clears .is-rewards-subpage via onRewardsScreen).
        const rw = this.querySelector('rewards-account');
        if (rw && typeof rw.showScreen === 'function') rw.showScreen('main');
      } else if (action === 'order-open') {
        const id = trigger.dataset.orderId;
        const order = (this.ordersData || []).find((o) => o.id === id);
        if (order) {
          this.detailReturn = this.currentView === 'orders' ? 'orders' : 'account';
          this.renderDetail(order);
          this.showView('order-detail');
        }
      } else if (action === 'detail-back') {
        this.showView(this.detailReturn || 'account');
      } else if (action === 'buy-again') {
        await this.buyAgain(trigger);
      } else if (action === 'copy-address') {
        const addr = this.detailOrder && this.detailOrder.fulfillment && this.detailOrder.fulfillment.address;
        if (addr) {
          const ok = await this.copyText(addr);
          const orig = trigger.textContent;
          trigger.textContent = ok ? 'Copied ✓' : 'Copy failed';
          setTimeout(() => {
            trigger.textContent = orig;
          }, 1800);
        }
      } else if (action === 'open-settings') {
        this.resetSettingsForm();
        this.showView('settings');
      } else if (action === 'close-settings') {
        this.showView('account');
      } else if (action === 'save-settings') {
        const displayName = (this.refs.nameInput.value || '').trim();
        const preferredEmail = (this.refs.prefEmailInput.value || '').trim();
        if (preferredEmail && !EMAIL_RE.test(preferredEmail)) {
          this.showError('Enter a valid email address.', this.refs.prefEmailError);
          return;
        }
        trigger.disabled = true;
        await window.CKCAuth.setProfile({ displayName, preferredEmail });
        this.profileBaseline = { displayName, preferredEmail };
        this.showSaved();
      } else if (action === 'profile-email-send') {
        const email = (this.refs.prefEmailInput.value || '').trim();
        if (!EMAIL_RE.test(email)) {
          this.showError('Enter a valid email address.', errEl);
          return;
        }
        trigger.disabled = true;
        await window.CKCAuth.sendProfileEmailCode(email);
        if (this.refs.evSent) this.refs.evSent.textContent = `Enter the code we sent to ${email}.`;
        if (this.refs.evSendStep) this.refs.evSendStep.hidden = true;
        if (this.refs.evCodeStep) this.refs.evCodeStep.hidden = false;
        if (this.refs.evCode) {
          this.refs.evCode.value = '';
          this.refs.evCode.focus();
        }
      } else if (action === 'profile-email-verify') {
        const email = (this.refs.prefEmailInput.value || '').trim();
        const code = (this.refs.evCode.value || '').replace(/\s+/g, '');
        if (!code) {
          this.showError('Enter the code we emailed you.', errEl);
          return;
        }
        trigger.disabled = true;
        const ok = await window.CKCAuth.verifyProfileEmailCode(code);
        if (ok) {
          this.verifiedEmail = email;
          this.resetEmailVerify();
        } else {
          this.showError("That code didn't match. Try again.", errEl);
        }
      } else if (action === 'save-address') {
        const form = this.getAddrForm();
        for (const f of ['address1', 'city', 'zip', 'countryCode']) {
          if (!form[f]) {
            this.showError('Please fill in the street, city, ZIP, and country.', this.refs.addrError);
            return;
          }
        }
        trigger.disabled = true;
        await window.CKCAuth.saveAddress(form);
        this.hasAddress = true;
        this.addrBaseline = form;
        if (this.refs.addrRemove) this.refs.addrRemove.hidden = false;
        if (this.refs.addrSaved) this.refs.addrSaved.hidden = false;
      } else if (action === 'remove-address') {
        if (!window.confirm('Remove your saved shipping address?')) return;
        trigger.disabled = true;
        await window.CKCAuth.deleteAddress();
        this.clearAddressFields();
      } else if (action === 'ac-pick') {
        const i = parseInt(trigger.dataset.acIndex, 10);
        const s = (this._acResults || [])[i];
        if (s) this.applySuggestion(s);
        this.hideAutocomplete();
      } else if (action === 'delete-start') {
        if (this.refs.deleteBtn) this.refs.deleteBtn.hidden = true;
        if (this.refs.deleteConfirm) this.refs.deleteConfirm.hidden = false;
      } else if (action === 'delete-cancel') {
        this.resetDelete();
      } else if (action === 'delete-confirm') {
        trigger.disabled = true;
        const status = await window.CKCAuth.deleteAccount(); // signs out → render() to signed-out
        window.alert(
          status === 'deleted'
            ? 'Your account has been deleted.'
            : "We've received your request and will remove your data shortly. You've been signed out."
        );
      } else if (action === 'signout') {
        if (this.refs.signoutBtn) this.refs.signoutBtn.hidden = true;
        if (this.refs.signoutConfirm) this.refs.signoutConfirm.hidden = false;
      } else if (action === 'signout-cancel') {
        this.resetSignout();
      } else if (action === 'signout-confirm') {
        this.ordersLoaded = false;
        this.ordersData = [];
        window.CKCAuth.signOut();
        this.resetSignout();
      }
    } catch (e) {
      this.showError(e.message || 'Something went wrong. Please try again.', errEl);
    } finally {
      trigger.disabled = false;
      // Re-assert the Save buttons' enabled/disabled state after any settings
      // action (the line above would otherwise always re-enable them).
      if (this.currentView === 'settings') {
        this.updateSettingsUI();
        this.updateAddrUI();
      }
    }
  }

  // Live settings updates as the user types.
  onInput(event) {
    if (this.currentView !== 'settings') return;
    const t = event.target;
    if (t && t.hasAttribute && t.hasAttribute('data-addr')) {
      if (t.getAttribute('data-addr') === 'address1') this.queryAutocomplete(t.value);
      this.clearAddrStatus();
      this.updateAddrUI();
      return;
    }
    if (t === this.refs.prefEmailInput) {
      // Editing the email invalidates any in-progress verification.
      this.resetEmailVerify();
    }
    this.updateSettingsUI();
  }

  // <select> changes (country/province) — rebuild the province field when the
  // country changes, then re-evaluate the address Save button.
  onChange(event) {
    if (this.currentView !== 'settings') return;
    const t = event.target;
    if (!t || !t.hasAttribute || !t.hasAttribute('data-addr')) return;
    if (t.hasAttribute('data-addr-country')) {
      this.renderProvinceField(t.value, '');
      this.hideAutocomplete();
    }
    this.clearAddrStatus();
    this.updateAddrUI();
  }

  onKeydown(event) {
    if (event.key !== 'Enter') return;
    if (event.target === this.refs.emailInput) {
      event.preventDefault();
      this.clickAction('email-start');
    } else if (event.target === this.refs.codeInput) {
      event.preventDefault();
      this.clickAction('email-verify');
    } else if (event.target === this.refs.nameInput || event.target === this.refs.prefEmailInput) {
      event.preventDefault();
      this.clickAction('save-settings');
    }
  }

  clickAction(action) {
    const b = this.querySelector(`[data-action="${action}"]`);
    if (b) b.click();
  }

  // Toggle the account view into "rewards sub-page" mode: when the rewards
  // module is showing its redeem or activity page, hide the sibling sections
  // (profile header, order history, sign-out) so only that page shows.
  onRewardsScreen(event) {
    const screen = event && event.detail && event.detail.screen;
    const sub = !!screen && screen !== 'main';
    if (this.refs.accountView) this.refs.accountView.classList.toggle('is-rewards-subpage', sub);
    if (sub && this.refs.rewardsSubtitle) {
      this.refs.rewardsSubtitle.textContent = screen === 'activity' ? 'Recent activity' : 'Redeem Paw Points';
    }
    // Jump back to the top when entering a sub-page (the panel may be scrolled).
    if (sub && this.refs.signedIn) this.refs.signedIn.scrollTop = 0;
  }

  showView(view) {
    this.currentView = view;
    const map = {
      account: this.refs.accountView,
      orders: this.refs.ordersView,
      'order-detail': this.refs.detailView,
      settings: this.refs.settingsView,
    };
    Object.keys(map).forEach((k) => {
      if (map[k]) map[k].hidden = k !== view;
    });
  }

  showCodeStep(email) {
    if (this.refs.emailSent) this.refs.emailSent.textContent = `Enter the code we sent to ${email}.`;
    if (this.refs.emailStep) this.refs.emailStep.hidden = true;
    if (this.refs.codeStep) this.refs.codeStep.hidden = false;
    if (this.refs.codeInput) {
      this.refs.codeInput.value = '';
      this.refs.codeInput.focus();
    }
  }

  resetEmail() {
    if (this.refs.codeStep) this.refs.codeStep.hidden = true;
    if (this.refs.emailStep) this.refs.emailStep.hidden = false;
    if (this.refs.codeInput) this.refs.codeInput.value = '';
  }

  showSaved() {
    if (this.refs.settingsError) this.refs.settingsError.hidden = true;
    if (this.refs.settingsSaved) this.refs.settingsSaved.hidden = false;
  }
  clearSettingsStatus() {
    if (this.refs.settingsError) this.refs.settingsError.hidden = true;
    if (this.refs.settingsSaved) this.refs.settingsSaved.hidden = true;
  }

  // Reload the settings form from the (synced) profile and reset verify state.
  resetSettingsForm() {
    const p = (window.CKCAuth && window.CKCAuth.getProfile && window.CKCAuth.getProfile()) || {};
    this.profileBaseline = {
      displayName: p.displayName || '',
      preferredEmail: p.preferredEmail || '',
    };
    this.verifiedEmail = null;
    if (this.refs.nameInput) this.refs.nameInput.value = this.profileBaseline.displayName;
    if (this.refs.prefEmailInput) this.refs.prefEmailInput.value = this.profileBaseline.preferredEmail;
    this.clearSettingsStatus();
    this.resetEmailVerify();
    this.updateSettingsUI();
    this.resetAddressForm();
  }

  // ---- shipping address ----
  countryOptions() {
    return COUNTRIES.map(([code, name]) => `<option value="${code}">${this.escape(name)}</option>`).join('');
  }

  // Builds the province field for a country: a <select> for US/CA, else a
  // free-text province-code input. `value` pre-selects/fills the current code.
  renderProvinceField(countryCode, value) {
    const el = this.refs.provinceField;
    if (!el) return;
    const list = provincesFor(countryCode);
    if (list) {
      const opts = ['<option value="">Select…</option>']
        .concat(
          list.map(
            ([code, name]) =>
              `<option value="${code}"${code === value ? ' selected' : ''}>${this.escape(name)}</option>`
          )
        )
        .join('');
      el.innerHTML = `<select class="custom-account__input custom-account__select" data-addr="provinceCode">${opts}</select>`;
    } else {
      el.innerHTML = `<input type="text" class="custom-account__input" data-addr="provinceCode" placeholder="State / province" value="${this.escape(value || '')}">`;
    }
  }

  // Reads the current address form values (trimmed) keyed by Shopify field name.
  getAddrForm() {
    const form = {};
    ADDR_KEYS.forEach((k) => {
      const el = this.querySelector(`[data-addr="${k}"]`);
      form[k] = el ? (el.value || '').trim() : '';
    });
    return form;
  }

  // Prefills the form from the synced saved address (cache) and sets the baseline.
  resetAddressForm() {
    const a = (window.CKCAuth && window.CKCAuth.getAddress && window.CKCAuth.getAddress()) || null;
    const country = (a && a.countryCode) || 'US';
    const setVal = (k, v) => {
      const el = this.querySelector(`[data-addr="${k}"]`);
      if (el) el.value = v || '';
    };
    setVal('firstName', a && a.firstName);
    setVal('lastName', a && a.lastName);
    setVal('address1', a && a.address1);
    setVal('address2', a && a.address2);
    setVal('city', a && a.city);
    setVal('countryCode', country);
    setVal('zip', a && a.zip);
    setVal('phone', a && a.phone);
    this.renderProvinceField(country, (a && a.provinceCode) || '');
    this.hasAddress = !!a;
    this.addrBaseline = this.getAddrForm();
    this.clearAddrStatus();
    this.hideAutocomplete();
    if (this.refs.addrRemove) this.refs.addrRemove.hidden = !this.hasAddress;
    this.updateAddrUI();
  }

  // Clears the form back to empty (US default) after a remove.
  clearAddressFields() {
    ADDR_KEYS.forEach((k) => {
      const el = this.querySelector(`[data-addr="${k}"]`);
      if (el) el.value = k === 'countryCode' ? 'US' : '';
    });
    this.renderProvinceField('US', '');
    this.hasAddress = false;
    this.addrBaseline = this.getAddrForm();
    if (this.refs.addrRemove) this.refs.addrRemove.hidden = true;
    this.clearAddrStatus();
    this.hideAutocomplete();
    this.updateAddrUI();
  }

  clearAddrStatus() {
    if (this.refs.addrError) this.refs.addrError.hidden = true;
    if (this.refs.addrSaved) this.refs.addrSaved.hidden = true;
  }

  // Enable Save only when the required fields are present AND something changed.
  updateAddrUI() {
    if (!this.refs.addrSave) return;
    const form = this.getAddrForm();
    const complete = !!(form.address1 && form.city && form.zip && form.countryCode);
    const base = this.addrBaseline || {};
    const dirty = ADDR_KEYS.some((k) => (form[k] || '') !== (base[k] || ''));
    this.refs.addrSave.disabled = !(complete && dirty);
  }

  // ---- address autocomplete ----
  // Debounced lookup as the customer types the street line.
  queryAutocomplete(text) {
    if (this._acTimer) clearTimeout(this._acTimer);
    const q = (text || '').trim();
    if (q.length < 3) {
      this.hideAutocomplete();
      return;
    }
    const countryEl = this.querySelector('[data-addr="countryCode"]');
    const country = (countryEl && countryEl.value ? countryEl.value : '').toLowerCase();
    this._acTimer = setTimeout(async () => {
      try {
        const list = await window.CKCAuth.autocompleteAddress(q, country);
        this._acResults = list || [];
        this.renderSuggestions(this._acResults);
      } catch (e) {
        this.hideAutocomplete();
      }
    }, 300);
  }

  renderSuggestions(list) {
    const el = this.refs.addrAc;
    if (!el) return;
    if (!list || !list.length) {
      this.hideAutocomplete();
      return;
    }
    el.innerHTML = list
      .map(
        (s, i) =>
          `<li><button type="button" class="custom-account__ac-item" data-action="ac-pick" data-ac-index="${i}">${this.escape(
            s.label
          )}</button></li>`
      )
      .join('');
    el.hidden = false;
  }

  hideAutocomplete() {
    if (this._acTimer) clearTimeout(this._acTimer);
    if (this.refs.addrAc) {
      this.refs.addrAc.hidden = true;
      this.refs.addrAc.innerHTML = '';
    }
  }

  // Fills the form from a chosen suggestion (rebuilding the province field if the
  // country changed).
  applySuggestion(s) {
    const setVal = (k, v) => {
      const el = this.querySelector(`[data-addr="${k}"]`);
      if (el && v) el.value = v;
    };
    setVal('address1', s.address1);
    setVal('city', s.city);
    setVal('zip', s.zip);
    if (s.countryCode) {
      const cc = this.querySelector('[data-addr="countryCode"]');
      if (cc) cc.value = s.countryCode;
      this.renderProvinceField(s.countryCode, s.provinceCode || '');
    } else if (s.provinceCode) {
      const pv = this.querySelector('[data-addr="provinceCode"]');
      if (pv) pv.value = s.provinceCode;
    }
    this.clearAddrStatus();
    this.updateAddrUI();
  }

  // Collapse the email-verify UI back to its initial "Verify this email" step.
  resetEmailVerify() {
    if (this.refs.evCodeStep) this.refs.evCodeStep.hidden = true;
    if (this.refs.evSendStep) this.refs.evSendStep.hidden = false;
    if (this.refs.evCode) this.refs.evCode.value = '';
    if (this.refs.evError) this.refs.evError.hidden = true;
  }

  // Recompute dirty/valid/verified state → toggle the verify UI + Save button.
  updateSettingsUI() {
    if (!this.refs.nameInput || !this.refs.prefEmailInput) return;
    const base = this.profileBaseline || { displayName: '', preferredEmail: '' };
    const nameVal = (this.refs.nameInput.value || '').trim();
    const emailVal = (this.refs.prefEmailInput.value || '').trim();
    const emailFormatOk = emailVal === '' || EMAIL_RE.test(emailVal);
    const nameChanged = nameVal !== (base.displayName || '');
    const emailChanged = emailVal !== (base.preferredEmail || '');
    const emailVerified = this.verifiedEmail !== null && this.verifiedEmail === emailVal;
    // A changed, non-empty, well-formed email must be verified before saving.
    const needsVerify = emailChanged && emailVal !== '' && emailFormatOk && !emailVerified;
    const emailSaveable = !emailChanged || emailVal === '' || (emailFormatOk && emailVerified);
    const dirty = nameChanged || emailChanged;

    if (this.refs.prefEmailError) {
      this.refs.prefEmailError.textContent = 'Enter a valid email address.';
      this.refs.prefEmailError.hidden = !(emailVal !== '' && !emailFormatOk);
    }
    if (this.refs.emailVerify) this.refs.emailVerify.hidden = !needsVerify;
    if (this.refs.evVerified) this.refs.evVerified.hidden = !(emailChanged && emailVerified);
    if (this.refs.saveBtn) this.refs.saveBtn.disabled = !(dirty && emailSaveable);
  }
  resetDelete() {
    if (this.refs.deleteConfirm) this.refs.deleteConfirm.hidden = true;
    if (this.refs.deleteBtn) this.refs.deleteBtn.hidden = false;
    if (this.refs.deleteError) this.refs.deleteError.hidden = true;
  }
  resetSignout() {
    if (this.refs.signoutConfirm) this.refs.signoutConfirm.hidden = true;
    if (this.refs.signoutBtn) this.refs.signoutBtn.hidden = false;
  }

  showError(msg, el) {
    el = el || this.refs.error;
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }
  clearError() {
    [
      this.refs.error,
      this.refs.emailError,
      this.refs.codeError,
      this.refs.settingsError,
      this.refs.evError,
      this.refs.deleteError,
      this.refs.addrError,
    ].forEach((el) => {
      if (el) el.hidden = true;
    });
  }

  template() {
    return `
      <div class="account-actions custom-account">
        <div data-state="loading" class="custom-account__loading">
          <span class="custom-account__muted">Loading…</span>
        </div>

        <div data-state="signed-out" hidden>
          <header class="account-actions__header">
            <h2 id="account-drawer-heading" class="account-actions__title h5">Sign in</h2>
            <span class="account-actions__email">Access your orders and check out faster.</span>
          </header>
          <div class="account-actions__sign-ins">
            <button type="button" class="button custom-account__provider" data-action="google">
              ${this.googleIcon()}<span>Continue with Google</span>
            </button>
            <button type="button" class="button button-secondary custom-account__provider" data-action="apple">
              ${this.appleIcon()}<span>Continue with Apple</span>
            </button>

            <div class="custom-account__email" data-email-block>
              <div class="custom-account__divider"><span>or</span></div>
              <div data-email-step="email">
                <input type="email" class="custom-account__input" data-email-input
                  placeholder="you@email.com" autocomplete="email" inputmode="email"
                  aria-label="Email address">
                <p class="custom-account__error" data-email-error hidden></p>
                <button type="button" class="button button-secondary custom-account__provider" data-action="email-start">
                  ${this.mailIcon()}<span>Email me a code</span>
                </button>
              </div>
              <div data-email-step="code" hidden>
                <p class="custom-account__muted" data-email-sent></p>
                <input type="text" class="custom-account__input" data-code-input
                  placeholder="Enter the 8-digit code" autocomplete="one-time-code" inputmode="numeric"
                  maxlength="8" aria-label="Sign-in code">
                <p class="custom-account__error" data-code-error hidden></p>
                <p class="custom-account__muted custom-account__hint">Don't see it? Check your spam or junk folder.</p>
                <button type="button" class="button custom-account__provider" data-action="email-verify">
                  <span>Verify &amp; sign in</span>
                </button>
                <button type="button" class="custom-account__linkbtn" data-action="email-restart">
                  Use a different email
                </button>
              </div>
            </div>

            <p class="custom-account__error" data-error hidden></p>
          </div>
        </div>

        <div data-state="signed-in" hidden>
          <!-- ACCOUNT VIEW -->
          <div data-view="account">
            <header class="account-actions__header custom-account__profile">
              <div class="custom-account__profile-text">
                <h2 class="account-actions__title h5" data-greeting>Your account</h2>
                <span class="account-actions__email" data-email></span>
              </div>
              <button type="button" class="custom-account__iconbtn" data-action="open-settings" aria-label="Settings">
                ${this.gearIcon()}
              </button>
            </header>

            <!-- Centered header shown only while the rewards module is on a
                 sub-page (Redeem / Recent activity). Mirrors the settings/orders
                 headers: back pinned left, title centered. Title text is set in
                 onRewardsScreen(); visibility is driven by the .is-rewards-subpage
                 class on this account view. -->
            <header class="account-actions__header custom-account__centered-head custom-account__rewards-subhead">
              <button type="button" class="custom-account__back custom-account__back--abs" data-action="rewards-back" aria-label="Back to account">
                ${this.backIcon()}<span>Back</span>
              </button>
              <h2 class="account-actions__title h5" data-rewards-subtitle>Kat's Rewards</h2>
            </header>

            <section class="custom-account__section custom-account__rewards-section">
              <h3 class="custom-account__section-title">Kat's Rewards</h3>
              <div data-rewards-slot>${this.rewardsSlotHtml()}</div>
            </section>

            <section class="custom-account__section">
              <div class="custom-account__section-head">
                <h3 class="custom-account__section-title">Order history</h3>
                <button type="button" class="custom-account__viewall custom-account__viewall--inline" data-action="orders-all" data-orders-viewall hidden>View all</button>
              </div>
              <div class="custom-account__orders" data-orders></div>
            </section>

            <nav class="account-actions__nav">
              <button type="button" class="button button-secondary custom-account__signout" data-action="signout" data-signout-trigger>
                Sign out
              </button>
              <div class="custom-account__confirm" data-signout-confirm hidden>
                <p class="custom-account__muted">Sign out of your account?</p>
                <div class="custom-account__confirm-actions">
                  <button type="button" class="button custom-account__confirm-btn" data-action="signout-confirm">
                    Sign out
                  </button>
                  <button type="button" class="button button-secondary custom-account__confirm-btn" data-action="signout-cancel">
                    Cancel
                  </button>
                </div>
              </div>
            </nav>
          </div>

          <!-- ALL ORDERS VIEW -->
          <div data-view="orders" hidden>
            <header class="account-actions__header custom-account__centered-head">
              <button type="button" class="custom-account__back custom-account__back--abs" data-action="orders-back" aria-label="Back to account">
                ${this.backIcon()}<span>Back</span>
              </button>
              <h2 class="account-actions__title h5">Order history</h2>
            </header>
            <section class="custom-account__section">
              <div class="custom-account__orders" data-orders-all></div>
            </section>
          </div>

          <!-- ORDER DETAIL VIEW -->
          <div data-view="order-detail" hidden>
            <header class="account-actions__header custom-account__centered-head">
              <button type="button" class="custom-account__back custom-account__back--abs" data-action="detail-back" aria-label="Back">
                ${this.backIcon()}<span>Back</span>
              </button>
              <h2 class="account-actions__title h5" data-detail-title>Order</h2>
            </header>
            <section class="custom-account__section" data-order-detail></section>
          </div>

          <!-- SETTINGS VIEW -->
          <div data-view="settings" hidden>
            <header class="account-actions__header custom-account__centered-head">
              <button type="button" class="custom-account__back custom-account__back--abs" data-action="close-settings" aria-label="Back to account">
                ${this.backIcon()}<span>Back</span>
              </button>
              <h2 class="account-actions__title h5">Settings</h2>
            </header>

            <section class="custom-account__section">
              <h3 class="custom-account__section-title">Profile</h3>
              <div class="custom-account__field">
                <label class="custom-account__label" for="ckc-name">Display name</label>
                <input id="ckc-name" type="text" class="custom-account__input" data-name-input
                  placeholder="What should we call you?" autocomplete="name" maxlength="60">
                <p class="custom-account__hint">Shown in your greeting.</p>
              </div>
              <div class="custom-account__field">
                <label class="custom-account__label" for="ckc-prefemail">Preferred email</label>
                <input id="ckc-prefemail" type="email" class="custom-account__input" data-prefemail-input
                  placeholder="you@email.com" autocomplete="email" inputmode="email">
                <p class="custom-account__error" data-prefemail-error hidden></p>
                <div class="custom-account__verify" data-email-verify hidden>
                  <div data-evstep="send">
                    <button type="button" class="button button-secondary custom-account__provider" data-action="profile-email-send">
                      Verify this email
                    </button>
                  </div>
                  <div data-evstep="code" hidden>
                    <p class="custom-account__muted" data-ev-sent></p>
                    <input type="text" class="custom-account__input" data-ev-code
                      placeholder="Enter the 8-digit code" autocomplete="one-time-code" inputmode="numeric"
                      maxlength="8" aria-label="Verification code">
                    <button type="button" class="button custom-account__provider" data-action="profile-email-verify">
                      Verify
                    </button>
                  </div>
                  <p class="custom-account__error" data-ev-error hidden></p>
                </div>
                <p class="custom-account__verified" data-ev-verified hidden>✓ Email verified</p>
                <p class="custom-account__hint">Preferred emails need to be verified.</p>
              </div>
              <p class="custom-account__hint custom-account__disclaimer">Your display name and preferred email are just for your greeting and contact. Your login email and account stay the same.</p>
              <p class="custom-account__error" data-settings-error hidden></p>
              <button type="button" class="button custom-account__provider" data-action="save-settings" disabled>Save changes</button>
              <p class="custom-account__saved" data-settings-saved hidden>Saved ✓</p>
            </section>

            <section class="custom-account__section">
              <h3 class="custom-account__section-title">Shipping address</h3>
              <div class="custom-account__addr-grid">
                <div class="custom-account__field">
                  <label class="custom-account__label" for="ckc-addr-first">First name</label>
                  <input id="ckc-addr-first" type="text" class="custom-account__input" data-addr="firstName" autocomplete="given-name">
                </div>
                <div class="custom-account__field">
                  <label class="custom-account__label" for="ckc-addr-last">Last name</label>
                  <input id="ckc-addr-last" type="text" class="custom-account__input" data-addr="lastName" autocomplete="family-name">
                </div>
              </div>
              <div class="custom-account__field">
                <label class="custom-account__label" for="ckc-addr-1">Address</label>
                <div class="custom-account__ac-wrap">
                  <input id="ckc-addr-1" type="text" class="custom-account__input" data-addr="address1" autocomplete="off" placeholder="Start typing your address…">
                  <ul class="custom-account__ac" data-addr-ac hidden></ul>
                </div>
                <input type="text" class="custom-account__input custom-account__input--stack" data-addr="address2" autocomplete="address-line2" placeholder="Apt, suite, unit (optional)">
              </div>
              <div class="custom-account__field">
                <label class="custom-account__label" for="ckc-addr-city">City</label>
                <input id="ckc-addr-city" type="text" class="custom-account__input" data-addr="city" autocomplete="address-level2">
              </div>
              <div class="custom-account__field">
                <label class="custom-account__label" for="ckc-addr-country">Country</label>
                <select id="ckc-addr-country" class="custom-account__input custom-account__select" data-addr="countryCode" data-addr-country>
                  ${this.countryOptions()}
                </select>
              </div>
              <div class="custom-account__addr-grid">
                <div class="custom-account__field">
                  <label class="custom-account__label">State / Province</label>
                  <div data-province-field></div>
                </div>
                <div class="custom-account__field">
                  <label class="custom-account__label" for="ckc-addr-zip">ZIP / Postal</label>
                  <input id="ckc-addr-zip" type="text" class="custom-account__input" data-addr="zip" autocomplete="postal-code">
                </div>
              </div>
              <div class="custom-account__field">
                <label class="custom-account__label" for="ckc-addr-phone">Phone (optional)</label>
                <input id="ckc-addr-phone" type="tel" class="custom-account__input" data-addr="phone" autocomplete="tel" inputmode="tel" placeholder="For delivery questions">
              </div>
              <p class="custom-account__error" data-addr-error hidden></p>
              <p class="custom-account__saved" data-addr-saved hidden>Saved ✓</p>
              <button type="button" class="button custom-account__provider" data-action="save-address" data-addr-save disabled>Save address</button>
              <button type="button" class="custom-account__linkbtn custom-account__deletelink" data-action="remove-address" data-addr-remove hidden>Remove saved address</button>
              <p class="custom-account__hint">Used to prefill your shipping details in our app at checkout. Saved to your account; the website checkout isn't prefilled yet.</p>
            </section>

            <section class="custom-account__section custom-account__danger">
              <h3 class="custom-account__section-title">Delete account</h3>
              <p class="custom-account__muted">Permanently deletes your account, your order history, and your saved information.</p>
              <button type="button" class="custom-account__linkbtn custom-account__deletelink" data-action="delete-start" data-delete-trigger>
                Delete account
              </button>
              <div class="custom-account__confirm" data-delete-confirm hidden>
                <p class="custom-account__muted">Delete your account and all order history? This can't be undone.</p>
                <div class="custom-account__confirm-actions">
                  <button type="button" class="button button-secondary custom-account__delete custom-account__confirm-btn" data-action="delete-confirm">
                    Delete everything
                  </button>
                  <button type="button" class="button button-secondary custom-account__confirm-btn" data-action="delete-cancel">
                    Cancel
                  </button>
                </div>
              </div>
              <p class="custom-account__error" data-delete-error hidden></p>
            </section>
          </div>
        </div>
      </div>
    `;
  }

  googleIcon() {
    return `<svg class="custom-account__provider-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62Z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18Z"/><path fill="#FBBC05" d="M3.98 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.3-1.72V4.94H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.06l3.02-2.34Z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58Z"/></svg>`;
  }
  appleIcon() {
    return `<svg class="custom-account__provider-icon" width="16" height="18" viewBox="0 0 16 18" aria-hidden="true" fill="currentColor"><path d="M13.3 9.54c-.02-2 1.63-2.96 1.7-3-.93-1.36-2.38-1.55-2.9-1.57-1.23-.12-2.4.72-3.03.72-.62 0-1.59-.7-2.62-.68-1.35.02-2.6.78-3.29 1.99-1.4 2.43-.36 6.03 1 8 .67.96 1.46 2.04 2.5 2 1-.04 1.39-.65 2.6-.65 1.21 0 1.56.65 2.62.63 1.08-.02 1.77-.98 2.43-1.95.77-1.12 1.08-2.2 1.1-2.26-.02-.01-2.1-.81-2.12-3.2Z"/><path d="M11.3 3.66c.55-.67.92-1.6.82-2.53-.79.03-1.76.53-2.33 1.2-.51.58-.96 1.53-.84 2.43.88.07 1.78-.45 2.35-1.1Z"/></svg>`;
  }
  mailIcon() {
    return `<svg class="custom-account__provider-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="1.75" y="3.25" width="14.5" height="11.5" rx="2"/><path d="m2.5 4.5 6.5 5 6.5-5"/></svg>`;
  }
  gearIcon() {
    return `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true"><path d="M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z"/><path d="M19.4 13a7.6 7.6 0 000-2l2-1.5-2-3.4-2.3 1a7.5 7.5 0 00-1.8-1l-.3-2.6H10l-.3 2.6a7.5 7.5 0 00-1.8 1l-2.3-1-2 3.4 2 1.5a7.6 7.6 0 000 2l-2 1.5 2 3.4 2.3-1c.5.4 1.1.8 1.8 1l.3 2.6h4l.3-2.6c.7-.2 1.3-.6 1.8-1l2.3 1 2-3.4-2-1.5z"/></svg>`;
  }
  backIcon() {
    return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>`;
  }
  // Two-layer gate for the Kat's Rewards module (mirrors the app + landing page):
  //   - rewardsEnabled(): the HARD master kill-switch (Liquid
  //     `settings.rewards_enabled`, surfaced via CKC_AUTH_CONFIG). When off, the
  //     module never shows the live UI.
  //   - rewardsLaunched(): the SERVER launch flag (REWARDS_LAUNCHED, surfaced via
  //     CKCAuth.launched from the public GET /api/rewards/config). Flipped
  //     server-side with no theme redeploy.
  // The live balance/ladder/redeem module renders only when BOTH are true;
  // otherwise the section shows a small "coming soon" note.
  rewardsEnabled() {
    return !!(window.CKC_AUTH_CONFIG && window.CKC_AUTH_CONFIG.rewardsEnabled);
  }
  rewardsLaunched() {
    return !!(window.CKCAuth && window.CKCAuth.launched);
  }

  // Markup for the rewards section body: the live <rewards-account> module when
  // enabled + launched, else the coming-soon note.
  rewardsSlotHtml() {
    if (this.rewardsEnabled() && this.rewardsLaunched()) {
      return '<rewards-account></rewards-account>';
    }
    return `<div class="custom-account__rewards">
      ${this.cookieIcon()}
      <div>
        <p class="custom-account__rewards-title">Rewards are coming soon</p>
        <p class="custom-account__muted">Earn points on every batch and unlock sweet rewards. Stay tuned!</p>
      </div>
    </div>`;
  }

  // Re-render the rewards section body when the launch flag resolves (the config
  // fetch is async, so it may flip after the initial template render). Only swaps
  // when the desired mode actually changes, so a live <rewards-account> isn't
  // needlessly torn down and rebuilt.
  renderRewardsSlot() {
    const slot = this.querySelector('[data-rewards-slot]');
    if (!slot) return;
    const mode = this.rewardsEnabled() && this.rewardsLaunched() ? 'live' : 'soon';
    if (this._rewardsMode === mode) return;
    this._rewardsMode = mode;
    slot.innerHTML = this.rewardsSlotHtml();
  }

  cookieIcon() {
    // Prefer the real cookie photo (URL injected from Liquid asset_url); fall
    // back to a drawn cookie if it isn't configured.
    const url =
      (window.CKCAuth && window.CKCAuth.config && window.CKCAuth.config.rewardsCookieUrl) || '';
    if (url) {
      return `<img class="custom-account__rewards-icon" src="${this.escape(url)}" width="40" height="40" alt="" aria-hidden="true">`;
    }
    return `<svg class="custom-account__rewards-icon" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true"><circle cx="16" cy="16" r="13" fill="#e9c79a" stroke="#c79a5c" stroke-width="1.5"/><circle cx="12" cy="12" r="1.6" fill="#7b4a1e"/><circle cx="20" cy="13" r="1.6" fill="#7b4a1e"/><circle cx="15" cy="19" r="1.6" fill="#7b4a1e"/><circle cx="21" cy="20" r="1.3" fill="#7b4a1e"/><circle cx="10" cy="18" r="1.3" fill="#7b4a1e"/></svg>`;
  }
}

if (!customElements.get('custom-account-menu')) {
  customElements.define('custom-account-menu', CustomAccountMenu);
}
